import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db, auth } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { useAuth } from "../hooks/useAuth";

const AgentDetail = () => {
  const { id } = useParams();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Register Agent fields
  const registerFields = [
    "Agent ID",
    "Agent Name",
    "Agent Type",
    "Version",
    "Organization",
    "Agent Description",
    "Intent Scope",
    "Language Models",
    "Guardrails",
    "Creation Date",
    "created_by_name",
  ];

  // Validation fields
  const validationFields = [
    "Reputation Score",
    "Failure Rate",
    "Violations",
    "Provenance",
    "Comments",
    "Verification Status",
  ];

  useEffect(() => {
    const fetchAgent = async () => {
      try {
        const docRef = doc(db, "agents", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setAgent(docSnap.data());
        } else {
          setAgent(null);
        }
      } catch (e) {
        setAgent(null);
      } finally {
        setLoading(false);
      }
    };
    fetchAgent();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="valia-spinner h-12 w-12 mx-auto mb-4"></div>
          <span className="text-valia-gray-600 text-lg">
            Loading agent details...
          </span>
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="text-center text-valia-gray-600 py-12 text-lg">
        <svg
          className="w-12 h-12 sm:w-16 sm:h-16 text-valia-gray-400 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <p className="text-base sm:text-lg mb-4">Agent not found.</p>
        <button
          className="valia-btn-primary valia-btn-sm"
          onClick={() => navigate(-1)}
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-valia-primary mb-2">
              Agent Details
            </h1>
            <p className="text-valia-gray-600 text-base">
              {agent["Agent Name"] || "Unnamed Agent"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {(() => {
              const status = agent["Verification Status"];
              if (status === "Verified" || status === "Active") {
                return (
                  <span className="valia-badge-success px-4 py-2 text-sm font-semibold border border-green-300">
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Verified
                  </span>
                );
              } else if (status === "Under Review") {
                return (
                  <span className="valia-badge-info px-4 py-2 text-sm font-semibold border border-blue-300">
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Under Review
                  </span>
                );
              } else if (status === "Pending") {
                return (
                  <span className="valia-badge-warning px-4 py-2 text-sm font-semibold border border-orange-300">
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Pending
                  </span>
                );
              } else if (status === "Unverified" || status === "Inactive") {
                return (
                  <span className="valia-badge-error px-4 py-2 text-sm font-semibold border border-red-300">
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    {status === "Inactive" ? "Inactive" : "Unverified"}
                  </span>
                );
              } else {
                return (
                  <span className="valia-badge-gray px-4 py-2 text-sm font-semibold border border-gray-400">
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    {status || "Unknown"}
                  </span>
                );
              }
            })()}
            <button
              onClick={() => navigate(`/agents/${id}/history`)}
              className="valia-btn-primary valia-btn-sm flex items-center gap-2 self-start sm:self-auto"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              View History
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-10">
        {/* Agent Registration Information */}
        <div className="valia-card">
          <div className="valia-card-header">
            <h3 className="text-lg font-semibold text-valia-gray-900">
              Agent Registration Information
            </h3>
          </div>
          <div className="valia-card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {registerFields.map((field) => (
                <div key={field} className="space-y-1">
                  <dt className="text-sm font-medium text-valia-gray-500">
                    {field}
                  </dt>
                  <dd className="text-sm text-valia-gray-900">
                    {typeof agent[field] === "boolean"
                      ? agent[field]
                        ? "Yes"
                        : "No"
                      : agent[field] || (
                          <span className="text-valia-gray-500">—</span>
                        )}
                  </dd>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Agent Validation Information */}
        <div className="valia-card">
          <div className="valia-card-header">
            <h3 className="text-lg font-semibold text-valia-gray-900">
              Agent Validation & Performance
            </h3>
          </div>
          <div className="valia-card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {validationFields.map((field) => (
                <div key={field} className="space-y-1">
                  <dt className="text-sm font-medium text-valia-gray-500">
                    {field}
                  </dt>
                  <dd className="text-sm text-valia-gray-900">
                    {typeof agent[field] === "boolean"
                      ? agent[field]
                        ? "Yes"
                        : "No"
                      : agent[field] || (
                          <span className="text-valia-gray-500">—</span>
                        )}
                  </dd>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentDetail;
